import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { OfferEditField } from "@/pages/recruit/offers/components/OfferEditField.tsx";
import { getOfferFieldNames } from "@/pages/recruit/offers/offer-edit-fields.ts";
import {
  ApiOfferVisibilityEnum,
  getOfferSalaryVisibilityTitle,
  useOfferEditContext,
} from "@/types/recruit/offer.types.ts";
import { Avatar, Box, Checkbox, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function OfferEditStepDetails() {
  const { t } = useTranslation();
  const { getSectionTitle } = useCustomFieldsHook();
  const { applicant, offerRequest, formHook, sections, formFields, handleUpdateConfig } =
    useOfferEditContext();

  const { watch } = formHook;

  const firstName = watch("first_name");
  const lastName = watch("last_name");
  const isJobTitleVisible = useMemo(
    () => getOfferFieldNames(formFields).has("job_title"),
    [formFields]
  );
  const jobTitle = watch("job_title");

  const salaryVisibilityOptions = useMemo(
    () =>
      Object.values(ApiOfferVisibilityEnum).map((value) => ({
        id: value,
        title: getOfferSalaryVisibilityTitle(value),
      })),
    []
  );

  return (
    <Stack gap="lg">
      {(applicant || offerRequest) && (
        <Stack gap={"md"}>
          <Avatar
            url={applicant?.photo_url || offerRequest?.offer_data.applicant.photo_url}
            acronym={`${firstName} ${lastName}`}
            size={"2xl"}
          />
          <Stack>
            <Text>{t("ats:invite_candidate")}</Text>
            <Title header={`${firstName} ${lastName}`} size={"lg"} />
            <Text className={"text-secondary"}>
              {isJobTitleVisible && jobTitle
                ? t("ats:join_us_as", { jobTitle: jobTitle })
                : t("ats:join_us")}
            </Text>
          </Stack>
        </Stack>
      )}
      {sections.map((section) => (
        <Stack key={section.id} gap={"smd"}>
          <Title size="xs" header={getSectionTitle(section)} />
          <Stack>
            {section.fields.map((field) => (
              <OfferEditField key={field.id} field={field} />
            ))}
          </Stack>
        </Stack>
      ))}
      <Stack gap={"smd"}>
        <Title paddingTop size={"xs"} header={t("ats:offer_visibility")} />
        <Controller
          control={formHook.control}
          name="config.visibility"
          render={({ field }) => (
            <Box variant={"border"}>
              <Stack gap={"sm"}>
                {salaryVisibilityOptions.map((option) => (
                  <Checkbox
                    key={option.id}
                    disabled={option.id === ApiOfferVisibilityEnum.only_me_admins_approvers}
                    checked={
                      option.id === ApiOfferVisibilityEnum.only_me_admins_approvers
                        ? true
                        : Array.isArray(field.value) && field.value.includes(option.id)
                    }
                    onChange={(e) => {
                      const newlyChecked = e.target.checked;
                      const clickedOptionId = option.id;
                      const currentValues = field.value as ApiOfferVisibilityEnum[];

                      let finalValues: ApiOfferVisibilityEnum[];

                      if (newlyChecked) {
                        finalValues = Array.from(new Set([...currentValues, clickedOptionId]));
                      } else {
                        finalValues = currentValues.filter((value) => value !== clickedOptionId);
                      }

                      field.onChange(finalValues);
                      handleUpdateConfig("visibility", finalValues);
                    }}
                    title={option.title}
                  />
                ))}
              </Stack>
            </Box>
          )}
        />
      </Stack>
    </Stack>
  );
}
